import type { MeResponse } from '@tabel/shared/schemas/auth';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Info, Plus, Search, Users } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, Callout, Field, PageHeader, SidePanel, inputCls, textareaCls } from '../../components/ui';
import { useSetCrumbs } from '../../lib/crumbs';
import {
  type Brigade,
  type Envelope,
  type Membership,
  type Page,
  type Preview,
  capabilities,
  command,
  dateTime,
  directoryRequest,
  queryString,
  today,
} from '../../lib/personnel-api';
import { BrigadePicker, useBrigadeOptions } from '../Personnel/BrigadePicker';
import {
  DateFields,
  DirectoryExport,
  ErrorNotice,
  Loading,
  NoResults,
  PanelSection,
  PreviewCommit,
  StateBadge,
  Table,
} from '../Personnel/parts';

type RosterPerson = {
  id: string;
  displayName: string | null;
  memberships?: Membership[];
  membership?: Membership | null;
};
export function BrigadesPage({ me }: { me: MeResponse }) {
  useSetCrumbs([{ label: 'Бригады' }]);
  const caps = capabilities(me);
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = useState(params.get('q') ?? '');
  const [searchField, setSearchField] = useState<'name' | 'leader'>('name');
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [from, setFrom] = useState(today());
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [createId, setCreateId] = useState(() => crypto.randomUUID());
  const set = (entries: Record<string, string | null>) =>
    setParams((old) => {
      const n = new URLSearchParams(old);
      for (const [key, value] of Object.entries(entries)) value ? n.set(key, value) : n.delete(key);
      return n;
    });
  const list = useQuery({
    queryKey: ['personnel', 'brigades', params.toString(), searchField],
    queryFn: () =>
      directoryRequest<Page<Brigade>>(
        `/api/brigades${queryString({ [searchField]: params.get('q') ?? undefined, mine: params.get('mine') ?? undefined, state: params.get('state') ?? undefined, cursor: params.get('cursor') ?? undefined })}`,
      ),
  });
  const changed = () => void qc.invalidateQueries({ queryKey: ['personnel'] });
  const create = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await directoryRequest<Brigade>(
        '/api/brigades',
        'POST',
        command({ mode: 'own', name: name.trim(), activeFromDate: from }, [], undefined, createId),
      );
      setCreating(false);
      setCreateId(crypto.randomUUID());
      set({ brigade: r.data.id });
      changed();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-6">
      <PageHeader
        title="Бригады"
        description="Бригады компании, их руководители и состав на организационную дату."
        actions={
          caps.includes('brigade.create_own') ? (
            <Button variant="primary" icon={Plus} onClick={() => setCreating(true)}>
              Создать свою бригаду
            </Button>
          ) : undefined
        }
      />
      <form
        className="grid grid-cols-1 gap-3 sm:grid-cols-2 wide:grid-cols-[minmax(0,1fr)_auto_auto_auto_auto] sm:items-end [&_label]:mb-0"
        onSubmit={(e) => {
          e.preventDefault();
          set({ q: search, cursor: null });
        }}
      >
        <div className="min-w-0 flex-1">
          <Field label="Найти бригаду или руководителя">
            <input
              aria-label="Найти бригаду или руководителя"
              className={inputCls}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Название или фамилия руководителя"
            />
          </Field>
        </div>
        <Field label="Искать по">
          <select
            aria-label="Искать по"
            className={inputCls}
            value={searchField}
            onChange={(e) => setSearchField(e.target.value as typeof searchField)}
          >
            <option value="name">Названию бригады</option>
            <option value="leader">Руководителю</option>
          </select>
        </Field>
        <Field label="Показывать">
          <select
            aria-label="Показывать"
            className={inputCls}
            value={params.get('mine') ?? ''}
            onChange={(e) => set({ mine: e.target.value, cursor: null })}
          >
            <option value="">Все доступные</option>
            <option value="true">Мои бригады</option>
          </select>
        </Field>
        <Field label="Состояние">
          <select
            aria-label="Состояние"
            className={inputCls}
            value={params.get('state') ?? ''}
            onChange={(e) => set({ state: e.target.value, cursor: null })}
          >
            <option value="">Все</option>
            <option value="active">Действуют</option>
            <option value="archived">В архиве</option>
          </select>
        </Field>
        <Button icon={Search} type="submit" className="sm:mb-0">
          Найти
        </Button>
      </form>
      <ErrorNotice error={list.error} retry={() => void list.refetch()} />
      {list.isLoading ? (
        <Loading />
      ) : (
        <>
          <Table label="Бригады компании">
            <thead>
              <tr>
                <th>Бригада</th>
                <th>Основной руководитель</th>
                <th>Состояние</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {list.data?.data.items.map((b) => (
                <tr key={b.id}>
                  <td
                    data-label="Бригада"
                    className="sticky max-sm:min-w-0 max-sm:pr-32 max-sm:whitespace-normal"
                  >
                    <button
                      type="button"
                      className="text-left font-medium hover:text-accent"
                      onClick={() => set({ brigade: b.id })}
                    >
                      {b.name}
                    </button>
                    <p className="mt-1 break-all text-xs text-ink-3">{b.id}</p>
                  </td>
                  <td data-label="Основной руководитель">{b.primaryLeader?.displayName ?? 'Не назначен'}</td>
                  <td data-label="Состояние">
                    <StateBadge state={b.state} />
                  </td>
                  <td className="act">
                    <Button size="sm" onClick={() => set({ brigade: b.id })}>
                      Состав и история
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          {!list.data?.data.items.length && !list.error && (
            <NoResults>
              {caps.includes('brigade.create_own')
                ? 'Создайте первую бригаду: название сохраняется независимо от руководителя.'
                : 'Здесь появятся бригады, доступные по вашим назначениям.'}
            </NoResults>
          )}
          {list.data?.data.nextCursor && (
            <Button onClick={() => set({ cursor: list.data?.data.nextCursor ?? null })}>
              Следующая страница
            </Button>
          )}
        </>
      )}
      {caps.includes('directory.export') && list.data && (
        <DirectoryExport
          kind="brigades"
          filters={{
            ...(params.get('q') && searchField === 'name' ? { query: params.get('q') as string } : {}),
            ...(params.get('state') ? { state: params.get('state') as string } : {}),
          }}
          snapshotRevision={list.data.data.snapshotRevision}
        />
      )}
      {params.get('brigade') && (
        <BrigadePanel
          key={params.get('brigade')}
          id={params.get('brigade') as string}
          caps={caps}
          onClose={() => set({ brigade: null })}
          onChanged={changed}
        />
      )}
      <SidePanel
        open={creating}
        onClose={() => setCreating(false)}
        title="Новая бригада"
        label="Создать свою бригаду"
        footer={
          <div className="flex justify-end gap-2">
            <Button onClick={() => setCreating(false)}>Отмена</Button>
            <Button variant="primary" disabled={busy || !name.trim() || !from} onClick={() => void create()}>
              {busy ? 'Создание…' : 'Создать свою бригаду'}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <Callout icon={Info}>
            Вы станете основным руководителем новой бригады. Можно создать несколько бригад с собственными
            названиями.
          </Callout>
          <Field label="Название бригады">
            <input
              aria-label="Название бригады"
              className={inputCls}
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setCreateId(crypto.randomUUID());
              }}
              placeholder="Например, Ух"
            />
          </Field>
          <Field label="Дата начала деятельности">
            <input
              aria-label="Дата начала деятельности"
              type="date"
              className={inputCls}
              value={from}
              onChange={(e) => {
                setFrom(e.target.value);
                setCreateId(crypto.randomUUID());
              }}
            />
          </Field>
          <p className="text-sm text-ink-2">
            Права руководителя начнут действовать после сохранения; дата деятельности не выдаёт права задним
            числом.
          </p>
          <ErrorNotice error={error} />
        </div>
      </SidePanel>
    </div>
  );
}

function BrigadePanel({
  id,
  caps,
  onClose,
  onChanged,
}: { id: string; caps: string[]; onClose: () => void; onChanged: () => void }) {
  const [onDate, setOnDate] = useState(today());
  const card = useQuery({
    queryKey: ['personnel', 'brigade', id],
    queryFn: () => directoryRequest<Brigade>(`/api/brigades/${encodeURIComponent(id)}`),
  });
  const members = useQuery({
    queryKey: ['personnel', 'brigade', id, 'members', onDate],
    queryFn: () =>
      directoryRequest<Page<Membership> & { operation_date: string }>(
        `/api/brigades/${encodeURIComponent(id)}/memberships?onDate=${encodeURIComponent(onDate)}`,
      ),
  });
  const destinations = useBrigadeOptions(
    'transfer',
    card.data?.data.allowedActions?.includes('membership.manage') ?? false,
  );
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState('');
  const [basis, setBasis] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState<'add' | 'end' | 'correct' | 'transfer' | 'archive' | 'restore' | null>(
    null,
  );
  const [personId, setPersonId] = useState('');
  const [membershipId, setMembershipId] = useState('');
  const [from, setFrom] = useState(today());
  const [to, setTo] = useState('');
  const [target, setTarget] = useState('');
  const [selectedDestination, setSelectedDestination] = useState<Brigade | null>(null);
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<RosterPerson[]>([]);
  const [preview, setPreview] = useState<Envelope<Preview> | null>(null);
  const [pendingInput, setPendingInput] = useState<unknown>(null);
  const [commitSummary, setCommitSummary] = useState<ReactNode>(null);
  const [endpoint, setEndpoint] = useState('');
  const b = card.data?.data;
  const allowed = b?.allowedActions ?? [];
  const changed = () => {
    onChanged();
    void card.refetch();
    void members.refetch();
  };
  const rename = async () => {
    if (!b) return;
    setBusy(true);
    try {
      await directoryRequest(
        `/api/brigades/${encodeURIComponent(id)}`,
        'PATCH',
        command({ name: name.trim(), basis: basis.trim() }, [{ kind: 'brigade', id, revision: b.revision }]),
      );
      setRenaming(false);
      changed();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };
  const prepare = async () => {
    if (!form) return;
    setError(null);
    setBusy(true);
    try {
      const path =
        form === 'transfer'
          ? '/api/brigades/transfers'
          : form === 'archive' || form === 'restore'
            ? `/api/brigades/${encodeURIComponent(id)}/activity`
            : `/api/brigades/${encodeURIComponent(id)}/memberships`;
      const input =
        form === 'transfer'
          ? { personId, fromBrigadeId: id, toBrigadeId: target, effectiveDate: from, basis: basis.trim() }
          : form === 'archive' || form === 'restore'
            ? { action: form, effectiveDate: from, basis: basis.trim() }
            : {
                action: form,
                personId,
                ...(membershipId ? { membershipId } : {}),
                dateRange: {
                  fromDate:
                    form === 'end'
                      ? (members.data?.data.items.find((m) => m.id === membershipId)?.fromDate ?? from)
                      : from,
                  toDateExclusive: form === 'end' ? from : to || null,
                },
                basis: basis.trim(),
              };
      const member = members.data?.data.items.find((m) => m.id === membershipId);
      const personName =
        results.find((p) => p.id === personId)?.displayName ?? member?.displayName ?? 'Имя не получено';
      const destination =
        selectedDestination?.id === target
          ? selectedDestination
          : destinations.items.find((d) => d.id === target);
      const summary = (
        <div className="space-y-2">
          <p className="font-medium">
            {
              {
                add: 'Добавить человека в бригаду',
                end: 'Завершить членство',
                correct: 'Исправить период членства',
                transfer: 'Перевести человека в другую бригаду',
                archive: 'Архивировать бригаду',
                restore: 'Возобновить деятельность',
              }[form]
            }
          </p>
          <p>
            Бригада: {b?.name ?? 'Название не получено'} · Brigade ID {id}
          </p>
          {personId && form !== 'archive' && form !== 'restore' && (
            <p>
              Человек: {personName} · Company ID {personId}
            </p>
          )}
          {membershipId && <p>Членство: {membershipId}</p>}
          {'dateRange' in input && input.dateRange && (
            <p>
              Период членства: {input.dateRange.fromDate} —{' '}
              {input.dateRange.toDateExclusive == null
                ? 'без ограничения конца'
                : `${input.dateRange.toDateExclusive} (окончание не включая)`}{' '}
              · организационные даты UTC
            </p>
          )}
          {form === 'transfer' && (
            <>
              <p>
                Куда: {destination?.name ?? 'Название не получено'} · Brigade ID {input.toBrigadeId}
              </p>
              {member && (
                <p>
                  Исходный период членства: {member.fromDate} —{' '}
                  {member.toDateExclusive == null
                    ? 'без ограничения конца'
                    : `${member.toDateExclusive} (окончание не включая)`}
                </p>
              )}
            </>
          )}
          {'effectiveDate' in input && (
            <p>Дата изменения: {input.effectiveDate} · организационная дата UTC</p>
          )}
          <p className="whitespace-pre-wrap break-words">Основание: {input.basis}</p>
        </div>
      );
      const result = await directoryRequest<Preview>(`${path}/preview`, 'POST', input);
      setCommitSummary(summary);
      setPreview(result);
      setEndpoint(path);
      setPendingInput(input);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };
  const start = (kind: typeof form, member?: Membership) => {
    setForm(kind);
    setMembershipId(member?.id ?? '');
    setPersonId(member?.personId ?? '');
    setFrom(kind === 'correct' ? (member?.fromDate ?? today()) : today());
    setTo(kind === 'correct' ? (member?.toDateExclusive ?? '') : '');
    setBasis('');
    setResults([]);
    setError(null);
  };
  return (
    <>
      <SidePanel open onClose={onClose} title={b?.name ?? 'Бригада'} label="Карточка бригады">
        <div className="space-y-5">
          <ErrorNotice error={card.error} retry={() => void card.refetch()} />
          {card.isLoading && <Loading />}
          {b && (
            <>
              <div className="space-y-2">
                <StateBadge state={b.state} />
                <p className="break-all text-xs text-ink-3">Brigade ID · {b.id}</p>
                <p className="text-sm">
                  Основной руководитель: <strong>{b.primaryLeader?.displayName ?? 'Не назначен'}</strong>
                </p>
              </div>
              <Callout icon={Info}>
                Название принадлежит бригаде. Работа на другой площадке не меняет членство. В состав
                подбираются существующие люди компании.
              </Callout>
              {allowed.includes('brigade.rename') && (
                <Button
                  onClick={() => {
                    setName(b.name);
                    setBasis('');
                    setRenaming(true);
                  }}
                >
                  Изменить название
                </Button>
              )}
              {renaming && (
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    void rename();
                  }}
                >
                  <Field label="Название бригады">
                    <input
                      aria-label="Название бригады"
                      className={inputCls}
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                    />
                  </Field>
                  <Field label="Основание переименования">
                    <textarea
                      aria-label="Основание переименования"
                      className={textareaCls}
                      value={basis}
                      onChange={(e) => setBasis(e.target.value)}
                    />
                  </Field>
                  <div className="flex gap-2">
                    <Button onClick={() => setRenaming(false)}>Отмена</Button>
                    <Button type="submit" variant="primary" disabled={busy || !name.trim() || !basis.trim()}>
                      Сохранить название
                    </Button>
                  </div>
                </form>
              )}
              <PanelSection title="Состав на дату">
                <Field label="Организационная дата">
                  <input
                    aria-label="Организационная дата"
                    type="date"
                    className={inputCls}
                    value={onDate}
                    onChange={(e) => setOnDate(e.target.value)}
                  />
                </Field>
                <ErrorNotice error={members.error} retry={() => void members.refetch()} />
                {members.isLoading && <Loading />}
                {members.data?.data.items.map((m) => (
                  <div key={m.id} className="space-y-2 border-b border-line pb-3">
                    <p className="text-sm font-medium">{m.displayName ?? m.personId}</p>
                    <p className="text-xs text-ink-3">
                      {m.fromDate} — {m.toDateExclusive ?? 'без окончания'} (не включая)
                    </p>
                    {allowed.includes('membership.manage') && (
                      <div className="flex flex-wrap gap-2">
                        <Button size="sm" onClick={() => start('end', m)}>
                          Завершить членство
                        </Button>
                        <Button size="sm" onClick={() => start('transfer', m)}>
                          Перевести
                        </Button>
                        {caps.includes('membership.correct_history') && (
                          <Button size="sm" onClick={() => start('correct', m)}>
                            Исправить период
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                ))}
                {!members.data?.data.items.length && !members.isLoading && !members.error && (
                  <p className="text-sm text-ink-2">На эту дату участников нет.</p>
                )}
                {allowed.includes('membership.manage') && (
                  <Button icon={Plus} onClick={() => start('add')}>
                    Добавить из справочника
                  </Button>
                )}
              </PanelSection>
              <PanelSection title="Периоды деятельности">
                {b.activity?.map((a, i) => (
                  <p key={`${a.fromDate}:${i}`} className="text-sm text-ink-2">
                    {a.fromDate} — {a.toDateExclusive ?? 'без окончания'} (не включая)
                  </p>
                ))}
                {allowed.includes('brigade.manage') && (
                  <Button onClick={() => start(b.state === 'archived' ? 'restore' : 'archive')}>
                    {b.state === 'archived' ? 'Возобновить деятельность' : 'Архивировать бригаду'}
                  </Button>
                )}
              </PanelSection>
              <PanelSection title="История бригады">
                {b.history?.length ? (
                  b.history.map((h) => (
                    <div key={h.id} className="border-b border-line pb-3 text-sm">
                      <p>
                        {(
                          {
                            brigade: 'Название и сведения',
                            membership: 'Состав',
                            activity: 'Деятельность',
                            assignment: 'Руководство и доступ',
                          } as Record<string, string>
                        )[h.entityKind] ?? 'Изменение справочника'}{' '}
                        · редакция {h.revision}
                      </p>
                      <p className="mt-1 text-xs text-ink-3">{dateTime(h.atMs)}</p>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-ink-2">История изменений пока пуста.</p>
                )}
              </PanelSection>
              {form && (
                <PanelSection
                  title={
                    {
                      add: 'Добавить человека',
                      end: 'Завершить членство',
                      correct: 'Исправить период членства',
                      transfer: 'Перевести в другую бригаду',
                      archive: 'Архивировать бригаду',
                      restore: 'Возобновить деятельность',
                    }[form]
                  }
                >
                  {members.data?.data.operation_date && (
                    <p className="text-sm text-ink-2">
                      Обычные изменения — с {members.data.data.operation_date} (UTC). Более ранние даты
                      требуют права на историю состава.
                    </p>
                  )}
                  {form === 'add' && (
                    <>
                      <form
                        onSubmit={async (e) => {
                          e.preventDefault();
                          try {
                            const r = await directoryRequest<Page<RosterPerson>>(
                              '/api/personnel/people/search',
                              'POST',
                              {
                                query: search,
                                purpose: 'roster',
                                targetBrigadeId: id,
                                dateRange: { fromDate: from, toDateExclusive: to || null },
                              },
                            );
                            setResults(r.data.items);
                          } catch (e) {
                            setError(e);
                          }
                        }}
                      >
                        <Field label="Найти человека для бригады">
                          <input
                            aria-label="Найти человека для бригады"
                            className={inputCls}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                          />
                        </Field>
                        <Button icon={Search} type="submit" disabled={!search.trim()}>
                          Найти в справочнике
                        </Button>
                      </form>
                      <Field label="Существующий человек">
                        <select
                          aria-label="Существующий человек"
                          className={inputCls}
                          value={personId}
                          onChange={(e) => setPersonId(e.target.value)}
                        >
                          <option value="">Выберите человека</option>
                          {results.map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.displayName ?? 'Имя не получено'}
                              {p.memberships?.length
                                ? ` · состоит в бригаде ${p.memberships[0]?.brigadeName ?? p.memberships[0]?.brigadeId}`
                                : ''}
                            </option>
                          ))}
                        </select>
                      </Field>
                    </>
                  )}
                  {form === 'transfer' && (
                    <BrigadePicker
                      label="Куда перевести"
                      searchLabel="Найти бригаду для перевода"
                      options={destinations}
                      value={target}
                      selected={selectedDestination}
                      excludeId={id}
                      requiredAction="membership.manage"
                      onSelect={(brigade) => {
                        setSelectedDestination(brigade);
                        setTarget(brigade?.id ?? '');
                      }}
                    />
                  )}
                  {form === 'add' || form === 'correct' ? (
                    <DateFields from={from} to={to} setFrom={setFrom} setTo={setTo} />
                  ) : (
                    <Field label={form === 'end' ? 'Дата окончания членства, не включая' : 'Дата изменения'}>
                      <input
                        aria-label={form === 'end' ? 'Дата окончания членства, не включая' : 'Дата изменения'}
                        type="date"
                        className={inputCls}
                        value={from}
                        onChange={(e) => {
                          setFrom(e.target.value);
                          if (form === 'end') setTo(e.target.value);
                        }}
                      />
                    </Field>
                  )}
                  {form === 'restore' && (
                    <Callout icon={Info}>Возобновление не возвращает прежний состав автоматически.</Callout>
                  )}
                  <Field label="Основание изменения">
                    <textarea
                      aria-label="Основание изменения"
                      className={textareaCls}
                      value={basis}
                      onChange={(e) => setBasis(e.target.value)}
                    />
                  </Field>
                  <div className="flex flex-wrap gap-2">
                    <Button onClick={() => setForm(null)}>Отмена</Button>
                    <Button
                      variant="primary"
                      disabled={
                        busy ||
                        !basis.trim() ||
                        !from ||
                        ((form === 'add' || form === 'end' || form === 'correct' || form === 'transfer') &&
                          !personId) ||
                        (form === 'transfer' && !target)
                      }
                      onClick={() => void prepare()}
                    >
                      {busy ? 'Проверка…' : 'Проверить последствия'}
                    </Button>
                  </div>
                </PanelSection>
              )}
              <ErrorNotice error={error} />
            </>
          )}
        </div>
      </SidePanel>
      {preview && (
        <PreviewCommit
          title="Подтвердить изменение бригады?"
          summary={commitSummary}
          preview={preview}
          input={pendingInput}
          endpoint={endpoint}
          onCancel={() => setPreview(null)}
          onDone={() => {
            setPreview(null);
            setForm(null);
            changed();
          }}
        />
      )}
    </>
  );
}
